import { Stack, Typography } from "@mui/material";
import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip, LoadingOverlay, TableColumn, TableComponent } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import {
	useGetAiModelsQuery,
	useUpdateAiModelMutation,
} from "@/pages/Private/redux/media/media.api";
import { AiModel } from "@/pages/Private/redux/media/media.schema";

import { AiModelModal } from "./AiModelModal";

/** Prices are stored in USD with 5 decimals; the registry is the only place they live. */
const formatPrice = (value: number, currency: string) =>
	new Intl.NumberFormat(undefined, {
		style: "currency",
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 5,
	}).format(value);

export const AiModelsTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`mediaPresets.${key}`);

	const [page, setPage] = useState(0);
	const [limit, setLimit] = useState(25);
	const [editing, setEditing] = useState<AiModel | null>(null);

	const { data: models, isLoading } = useGetAiModelsQuery();
	const [updateModel] = useUpdateAiModelMutation();

	const columns: TableColumn<AiModel>[] = useMemo(
		() => [
			{
				label: ts("columns.model"),
				minWidth: 220,
				format: (row) => (
					<Stack direction="column">
						<Stack alignItems="center" direction="row" spacing={1}>
							<Typography className="text-ink" variant="subtitle2">
								{row.displayName}
							</Typography>
							{row.isDefault ? <Chip color="success" label={ts("status.default")} /> : null}
						</Stack>
						<Typography className="text-subtle" fontWeight="400" variant="body2">
							{ts(`useCase.${row.useCase.toLowerCase()}`)}
						</Typography>
					</Stack>
				),
			},
			{
				label: ts("columns.provider"),
				minWidth: 120,
				format: (row) => ts(`provider.${row.provider.toLowerCase()}`),
			},
			{
				label: ts("columns.price"),
				minWidth: 180,
				format: (row) => (
					<Stack alignItems="flex-start" direction="column" spacing={0.5}>
						<Typography className="text-ink" variant="subtitle2">
							{formatPrice(row.unitPriceUsd, row.currency)} · {ts(`unit.${row.unit.toLowerCase()}`)}
						</Typography>
						{!row.pricesVerified ? (
							<Chip color="warning" label={ts("status.priceUnverified")} />
						) : null}
					</Stack>
				),
			},
			{
				label: ts("columns.status"),
				minWidth: 110,
				format: (row) => (
					<Chip
						color={row.enabled ? "success" : "secondary"}
						label={row.enabled ? ts("status.enabled") : ts("status.disabled")}
					/>
				),
			},
			{
				label: "",
				minWidth: 200,
				align: "right",
				format: (row) => (
					<Stack direction="row" justifyContent="flex-end" spacing={1}>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.XS}
							title={ts("actions.edit")}
							type="button"
							onClick={() => setEditing(row)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={row.isDefault || !row.enabled}
							size={ButtonSize.XS}
							title={ts("actions.makeDefault")}
							type="button"
							onClick={() => updateModel({ id: row.id, isDefault: true })}
						/>
					</Stack>
				),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	);

	return (
		<section className="rounded-card border border-border-soft bg-white p-5">
			<div className="mb-4">
				<h3 className="text-ink">{ts("models.title")}</h3>
				<p className="mt-1 text-sm text-subtle">{ts("models.description")}</p>
			</div>

			{isLoading ? <LoadingOverlay /> : null}

			<TableComponent<AiModel>
				columns={columns}
				data={models ?? []}
				handleChangeLimit={(event) => {
					setLimit(+event.target.value);
					setPage(0);
				}}
				handleChangePage={(_, nextPage) => setPage(nextPage)}
				isLoading={isLoading}
				itemCount={models?.length ?? 0}
				limit={limit}
				noDataText={ts("models.empty")}
				page={page}
			/>

			<AiModelModal
				handleClose={() => setEditing(null)}
				isOpened={Boolean(editing)}
				model={editing}
			/>
		</section>
	);
};
